// src/features/agent-builder/components/ConnectSphereTeaser.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Tooltip } from '@dopomogai/ui';
import { renderIcon } from '@dopomogai/utils';
import { MessageSquare, ShoppingCart, Settings } from 'lucide-react'; // Example, use renderIcon

interface IntegrationTeaserItem {
  id: string;
  nameKey: string; // i18n key for the integration name
  iconName: string; // Icon name from your iconMapping
}

const defaultIntegrations: IntegrationTeaserItem[] = [
  { id: 'facebook', nameKey: 'step5.connectSphere.integrations.facebook', iconName: 'message' }, // Assuming 'message' maps to a Facebook-like icon
  { id: 'whatsapp', nameKey: 'step5.connectSphere.integrations.whatsapp', iconName: 'messagecircle' }, // Or a specific WhatsApp icon if mapped
  { id: 'shopify', nameKey: 'step5.connectSphere.integrations.shopify', iconName: 'shoppingcart' },
  { id: 'slack', nameKey: 'step5.connectSphere.integrations.slack', iconName: 'message' }, // Example, use appropriate mapped icon
  { id: 'customapi', nameKey: 'step5.connectSphere.integrations.customApi', iconName: 'code' },
];


export const ConnectSphereTeaser: React.FC = () => {
  const { t } = useTranslation('agentBuilder');

  return (
    <div className="mt-8 pt-6 border-t border-gray-200">
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 text-sky-600 mb-2">
            {renderIcon('puzzle', { size: 28})} {/* 'puzzle' or 'network' */}
            <h3 className="text-xl font-semibold text-gray-800">
            {/* i18n: agentBuilder.step5.connectSphere.subheading = "ConnectSphere: More Ways to Interact" */}
            {t('step5.connectSphere.subheading')}
            </h3>
        </div>
        <p className="text-sm text-gray-600 max-w-md mx-auto">
          {/* i18n: agentBuilder.step5.connectSphere.description = "Extend your agent's reach. Our ConnectSphere assistant will soon guide you in connecting to popular platforms and services." */}
          {t('step5.connectSphere.description')}
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {defaultIntegrations.map((item) => (
          <div
            key={item.id}
            className="p-4 bg-slate-50 border border-slate-200 rounded-lg flex flex-col items-center text-center shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="p-3 mb-3 bg-white rounded-full border-2 border-slate-300 text-slate-600">
              {renderIcon(item.iconName, { size: 28 })}
            </div>
            <p className="text-sm font-medium text-gray-700 mb-2 h-10 flex items-center justify-center"> {/* Fixed height for name */}
              {t(item.nameKey)}
            </p>
            <Tooltip 
              textKey="agentBuilder:step5.connectSphere.configureTooltip" 
              textOptions={{platformName: t(item.nameKey)}}
            >
              <Button
                textKey="agentBuilder:step5.connectSphere.configureButton" // "[Configure (Coming Soon)]"
                variant="outline"
                size="sm"
                disabled
                icon={renderIcon('settings', { size: 14 })}
                className="w-full opacity-70 cursor-not-allowed border-slate-300 text-slate-500"
              />
            </Tooltip>
          </div>
        ))}
         {/* "Explore All" card - could be styled differently or be a button below */}
        <div className="p-4 bg-sky-50 border-2 border-sky-300 border-dashed rounded-lg flex flex-col items-center justify-center text-center shadow-sm hover:shadow-md transition-shadow">
             <div className="p-3 mb-3 bg-white rounded-full border-2 border-sky-400 text-sky-600">
                {renderIcon('zap', { size: 28})}
            </div>
            <p className="text-sm font-medium text-sky-700 mb-2">
                {/* i18n: agentBuilder.step5.connectSphere.exploreTitle = "Many More Coming" */}
                {t('step5.connectSphere.exploreTitle')}
            </p>
            <Tooltip textKey="agentBuilder:step5.connectSphere.exploreTooltip">
                <Button
                    textKey="agentBuilder:step5.connectSphere.exploreButton" // "[✨ Explore All Connections (Coming Soon)]"
                    variant="secondary"
                    size="sm"
                    disabled
                    icon={renderIcon('puzzle', {size: 14})}
                    className="w-full opacity-80 cursor-not-allowed border-sky-400 text-sky-700"
                />
            </Tooltip>
        </div>
      </div>
    </div>
  );
};